import { UserGroupIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import ShareWithUsers from '@/components/GenericForm/ShareOwnership'
import { RemoveCircle } from '@/components/Icons'
import Label from '@/components/Label'
import Modal from '@/components/Modal'
import Button from '@/components/TailwindUI/Button'
import messages from '@/messages'

interface ShareOwnershipModalProps {
  name?: string
  descriptor?: string
  title: string
  isOpen: boolean
  onClose: () => void
  sharedUserList: string[]
  onSubmit: (data: string[]) => void
  readOnly?: boolean
  appQueryResult?: any
}

interface ShareWithHookFormProps {
  name: string
  descriptor?: string
  sharedUserList: string[]
  onCancel: () => void
  onSubmit: (data: any) => void
  readOnly: boolean
  appQueryResult?: any
}

const ShareOwnershipModal = ({
  name = 'sharedWith',
  title,
  descriptor,
  isOpen = true,
  onClose,
  sharedUserList,
  onSubmit,
  readOnly = false,
  appQueryResult
}: ShareOwnershipModalProps) => {
  const closeIt = () => {
    onClose()
  }
  return (
    <Modal isOpen={isOpen} onClose={closeIt} size={readOnly ? 'xs' : 'sm'}>
      <div className="flex items-center justify-between pt-4">
        <div className="flex pl-3">
          <div className="row-auto mx-1 h-6 w-6 text-brand-400">
            <UserGroupIcon />
          </div>
          <Label className={'text-base'} htmlFor={name}>
            {title}
          </Label>
        </div>
        <Button className="" onClick={() => onClose()} color="transparent">
          <RemoveCircle />
        </Button>
      </div>
      <div className="px-4">
        <ShareWithHookForm
          name={name}
          descriptor={descriptor}
          sharedUserList={sharedUserList}
          onCancel={onClose}
          onSubmit={onSubmit}
          readOnly={readOnly}
          appQueryResult={appQueryResult}
        />
      </div>
    </Modal>
  )
}

function ShareWithHookForm({
  name,
  descriptor,
  sharedUserList,
  onCancel,
  onSubmit,
  readOnly,
  appQueryResult
}: ShareWithHookFormProps) {
  const {
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm()
  const intl = useIntl()
  const [sharedWith, setSharedWith] = useState(getValues(name) || sharedUserList)
  const handleFormSubmit = () => {
    onSubmit(sharedWith)
  }
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div>
        <ShareWithUsers
          formatProps={{ name, error: errors[name]?.message as string }}
          descriptor={descriptor}
          value={sharedWith}
          onSelect={userIds => {
            setSharedWith(userIds)
          }}
          disabled={readOnly}
          shouldDisplayLabel={false}
          appQueryResult={appQueryResult}
        />
      </div>
      {!readOnly && (
        <div className="mt-2 flex items-center justify-end gap-x-2 pr-4">
          <Button color={'secondary'} onClick={() => onCancel()}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button color={'primary'} type={'submit'}>
            {intl.formatMessage(messages.save)}
          </Button>
        </div>
      )}
    </form>
  )
}

export default ShareOwnershipModal
